import moment from 'moment';

const isoDateFormat = "YYYY-MM-DD";
const firstPage = 1;
const trueValue = "true";
const falseValue = "false";

// The viewer's own vocabulary for its URL and filters. RulesEngineService translates it for the endpoint.
export const comparisonModes = {
    legacyVersusShadow: "legacy-v-shadow",
    underTest: "under-test"
};

export const labelFilters = {
    untriaged: "untriaged",
    knownDifference: "known-difference",
    regression: "regression"
};

export const defaultDiffViewerQuery = {
    from: null,
    to: null,
    mode: comparisonModes.legacyVersusShadow,
    type: null,
    label: null,
    employee: null,
    provisional: true,
    whitelisted: false,
    signedOff: false,
    group: null,
    page: firstPage
};

export const positiveIntegerPattern = /^[1-9]\d*$/;

function parseDate(value) {
    if (value == null) {
        return null;
    }

    const date = moment(value, isoDateFormat, true);

    if (!date.isValid()) {
        return null;
    }

    return date.format(isoDateFormat);
}

function parsePositiveInteger(value) {
    if (value == null || !positiveIntegerPattern.test(value)) {
        return null;
    }

    return Number(value);
}

// A period is only meaningful whole and in order; anything less is treated as no period chosen.
function parsePeriod(searchParams) {
    const from = parseDate(searchParams.get("from"));
    const to = parseDate(searchParams.get("to"));

    if (from == null || to == null || moment(to, isoDateFormat).isBefore(moment(from, isoDateFormat))) {
        return { from: null, to: null };
    }

    return { from: from, to: to };
}

function parseMode(value) {
    if (Object.values(comparisonModes).includes(value)) {
        return value;
    }

    return defaultDiffViewerQuery.mode;
}

function parseLabel(value) {
    if (Object.values(labelFilters).includes(value)) {
        return value;
    }

    return null;
}

function parseGroup(value) {
    if (value == null || value.trim() === "") {
        return null;
    }

    return value;
}

/**
 * Reads the viewer's whole view from the URL. Anything missing or malformed takes its default, so a
 * hand-edited link still opens a view rather than an error.
 */
export function parseDiffViewerQuery(searchParams) {
    const period = parsePeriod(searchParams);

    return {
        from: period.from,
        to: period.to,
        mode: parseMode(searchParams.get("mode")),
        type: parsePositiveInteger(searchParams.get("type")),
        label: parseLabel(searchParams.get("label")),
        employee: parsePositiveInteger(searchParams.get("employee")),
        provisional: searchParams.get("provisional") !== falseValue,
        whitelisted: searchParams.get("whitelisted") === trueValue,
        signedOff: searchParams.get("signedoff") === trueValue,
        group: parseGroup(searchParams.get("group")),
        page: parsePositiveInteger(searchParams.get("page")) ?? firstPage
    };
}

/**
 * Writes a view back to URL parameters, leaving out every value at its default so the link stays as
 * short as the view allows.
 */
export function buildDiffViewerSearchParams(query) {
    const searchParams = new URLSearchParams();

    function setWhen(condition, name, value) {
        if (condition) {
            searchParams.set(name, `${value}`);
        }
    }

    setWhen(query.from != null && query.to != null, "from", query.from);
    setWhen(query.from != null && query.to != null, "to", query.to);
    setWhen(query.mode !== defaultDiffViewerQuery.mode, "mode", query.mode);
    setWhen(query.type != null, "type", query.type);
    setWhen(query.label != null, "label", query.label);
    setWhen(query.employee != null, "employee", query.employee);
    setWhen(query.provisional === false, "provisional", falseValue);
    setWhen(query.whitelisted === true, "whitelisted", trueValue);
    setWhen(query.signedOff === true, "signedoff", trueValue);
    setWhen(query.group != null, "group", query.group);
    setWhen(query.page > firstPage, "page", query.page);

    return searchParams;
}

// The filters the reads are made with. The open group and its page only narrow what is rendered.
export function buildDiffFilters(query) {
    return {
        mode: query.mode,
        from: query.from,
        to: query.to,
        typeId: query.type,
        label: query.label,
        employeeId: query.employee,
        includeProvisional: query.provisional,
        includeWhitelisted: query.whitelisted,
        includeSignedOff: query.signedOff
    };
}
